import { useEffect } from 'react';

export interface HotkeyOptions {
  /** Run the handler even if focus is inside an input/textarea. Default false. */
  allowInInputs?: boolean;
  /** Require the meta or ctrl modifier (e.g. ⌘K). Default false. */
  meta?: boolean;
  /** When false, the listener is detached. Default true. */
  enabled?: boolean;
}

/**
 * Bind a single keyboard shortcut. Pass an array of keys to map several at once.
 * Comparison is case-insensitive on `event.key`.
 */
export function useHotkey(
  keys: string | string[],
  handler: (event: KeyboardEvent) => void,
  { allowInInputs = false, meta = false, enabled = true }: HotkeyOptions = {},
): void {
  useEffect(() => {
    if (!enabled) return;
    const set = new Set(
      (Array.isArray(keys) ? keys : [keys]).map((k) => k.toLowerCase()),
    );
    const onKey = (e: KeyboardEvent) => {
      if (meta && !(e.metaKey || e.ctrlKey)) return;
      if (!meta && (e.metaKey || e.ctrlKey || e.altKey)) return;
      if (!allowInInputs) {
        const tag = (e.target as HTMLElement)?.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
        if ((e.target as HTMLElement)?.isContentEditable) return;
      }
      if (!set.has(e.key.toLowerCase())) return;
      handler(e);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [keys, handler, allowInInputs, meta, enabled]);
}
